// src/pages/KarigarManagement/Job/Tabs/JobPaymentsTab.jsx
// ============================================================================
// COMPLETE PAYMENT MANAGEMENT TAB - FIXED VERSION
// ============================================================================
// Fixes:
// 1. Handle field name mismatches (laborAmount vs actualLabor, type vs deductionType)
// 2. Better fallback handling for all calculations
// 3. Smart calculation when backend returns 0
// 4. Handle both old and new response formats
// 5. All existing functionality preserved
// ============================================================================

import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { Button } from "@/components/ui-v2/button";
import { Input } from "@/components/ui-v2/input";
import { Label } from "@/components/ui-v2/label";
import { Textarea } from "@/components/ui-v2/textarea";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui-v2/card";
import { Badge } from "@/components/ui-v2/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui-v2/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui-v2/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui-v2/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui-v2/alert-dialog";
import { Separator } from "@/components/ui-v2/separator";
import {
  Plus,
  Calculator,
  CheckCircle,
  Receipt,
  Wallet,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Edit,
  CreditCard,
  Clock,
  AlertCircle,
  Loader2,
  AlertTriangle,
  Eye,
  Trash2,
  Ban,
  Info,
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

// ============================================================================
// CONSTANTS
// ============================================================================

const LABOR_RATE_TYPES = [
  { value: "PER_GRAM", label: "Per Gram", unit: "₹/g" },
  { value: "FIXED", label: "Fixed Amount", unit: "₹" },
  { value: "PER_PIECE", label: "Per Piece", unit: "₹/pc" },
  { value: "PERCENTAGE", label: "Percentage of Material", unit: "%" },
  { value: "HOURLY", label: "Hourly Rate", unit: "₹/hr" },
];

const DEDUCTION_TYPES = [
  { value: "MATERIAL_SHORTAGE", label: "Material Shortage" },
  { value: "MATERIAL_LOSS", label: "Material Loss" },
  { value: "QUALITY_ISSUE", label: "Quality Issue" },
  { value: "DELAY_PENALTY", label: "Delay Penalty" },
  { value: "LATE_DELIVERY", label: "Late Delivery" },
  { value: "ADVANCE_RECOVERY", label: "Advance Recovery" },
  { value: "DAMAGE", label: "Damage" },
  { value: "OTHER", label: "Other" },
];

const PAYMENT_TYPES = [
  { value: "JOB_PAYMENT", label: "Job Payment" },
  { value: "ADVANCE", label: "Advance Payment" },
  { value: "SETTLEMENT", label: "Settlement" },
  { value: "BONUS", label: "Bonus" },
  { value: "ADJUSTMENT", label: "Adjustment" },
];

const PAYMENT_METHODS = [
  { value: "CASH", label: "Cash" },
  { value: "BANK_TRANSFER", label: "Bank Transfer" },
  { value: "UPI", label: "UPI" },
  { value: "CHEQUE", label: "Cheque" },
  { value: "NEFT", label: "NEFT" },
  { value: "RTGS", label: "RTGS" },
  { value: "IMPS", label: "IMPS" },
];

const PAYMENT_STATUS_CONFIG = {
  PENDING: { bg: "bg-yellow-100", text: "text-yellow-800", label: "Pending" },
  APPROVED: { bg: "bg-blue-100", text: "text-blue-800", label: "Approved" },
  PROCESSING: {
    bg: "bg-purple-100",
    text: "text-purple-800",
    label: "Processing",
  },
  COMPLETED: { bg: "bg-green-100", text: "text-green-800", label: "Completed" },
  PAID: { bg: "bg-green-100", text: "text-green-800", label: "Paid" },
  ON_HOLD: { bg: "bg-gray-100", text: "text-gray-800", label: "On Hold" },
  DISPUTED: { bg: "bg-orange-100", text: "text-orange-800", label: "Disputed" },
  REJECTED: { bg: "bg-red-100", text: "text-red-800", label: "Rejected" },
  CANCELLED: { bg: "bg-red-100", text: "text-red-800", label: "Cancelled" },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return "-";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount || 0);
};

const formatWeight = (weight) => {
  if (!weight && weight !== 0) return "-";
  return `${parseFloat(weight).toFixed(3)}g`;
};

const getStatusConfig = (status) => {
  const normalizedStatus = status?.toUpperCase()?.replace(/ /g, "_");
  return (
    PAYMENT_STATUS_CONFIG[normalizedStatus] || {
      bg: "bg-gray-100",
      text: "text-gray-800",
      label: status || "Unknown",
    }
  );
};

const isPaidStatus = (status) => {
  const upper = status?.toUpperCase();
  return upper === "COMPLETED" || upper === "PAID";
};

const isPendingStatus = (status) => {
  const upper = status?.toUpperCase();
  return upper === "PENDING" || upper === "APPROVED";
};

const canCancelPayment = (status) => {
  const upper = status?.toUpperCase();
  return upper === "PENDING" || upper === "ON_HOLD";
};

// Normalize deduction type field
const normalizeDeduction = (d) => ({
  ...d,
  type: d.type || d.deductionType || "OTHER",
});

// Get labor rate type label
const getRateTypeLabel = (rateType) => {
  const found = LABOR_RATE_TYPES.find((t) => t.value === rateType);
  return found ? found.label : rateType?.replace(/_/g, " ") || "-";
};

// Get labor rate type unit
const getRateTypeUnit = (rateType) => {
  const found = LABOR_RATE_TYPES.find((t) => t.value === rateType);
  return found ? found.unit : "₹";
};

// ============================================================================
// PRICING PREVIEW DIALOG COMPONENT
// ============================================================================

const PricingPreviewDialog = ({
  open,
  onOpenChange,
  pricingPreview,
  job,
  onApplyRateType,
}) => {
  if (!pricingPreview) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pricing Preview</DialogTitle>
          </DialogHeader>
          <div className="text-center py-8 text-muted-foreground">
            <Loader2 className="h-8 w-8 mx-auto mb-2 animate-spin" />
            <p>Loading pricing preview...</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const {
    currentRateType,
    laborRate,
    availableValues = {},
    allRateTypePreviews = {},
    missingFields = [],
    recommendation,
    storedPricing,
  } = pricingPreview;

  const rateTypes = LABOR_RATE_TYPES;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            Pricing Preview - {job?.jobNumber}
          </DialogTitle>
          <DialogDescription>
            Preview labor charges for different rate types
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Available Values */}
          <div className="p-4 bg-muted/50 rounded-lg">
            <h4 className="font-medium mb-3 text-sm">
              Available Data for Calculation
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div>
                <span className="text-muted-foreground">Finished Weight:</span>
                <p className="font-medium">
                  {availableValues.finishedWeight ? (
                    `${availableValues.finishedWeight}g`
                  ) : (
                    <span className="text-amber-600">Not set</span>
                  )}
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">Piece Count:</span>
                <p className="font-medium">{availableValues.pieceCount || 1}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Material Value:</span>
                <p className="font-medium">
                  {availableValues.materialValue ? (
                    formatCurrency(availableValues.materialValue)
                  ) : (
                    <span className="text-amber-600">Not set</span>
                  )}
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">Actual Hours:</span>
                <p className="font-medium">
                  {availableValues.actualHours ? (
                    `${availableValues.actualHours}h`
                  ) : (
                    <span className="text-amber-600">Not set</span>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Warnings */}
          {missingFields.length > 0 && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">
                  Some rate types cannot be calculated
                </p>
                <p className="text-xs text-amber-700 mt-1">
                  Missing: {missingFields.join(", ")}
                </p>
              </div>
            </div>
          )}

          {recommendation && (
            <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <TrendingUp className="h-4 w-4 text-blue-600 mt-0.5" />
              <p className="text-sm text-blue-800">{recommendation}</p>
            </div>
          )}

          {/* Rate Type Cards */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm">Rate Type Calculations</h4>
            <div className="grid gap-3">
              {rateTypes.map(({ value: key, label, unit }) => {
                const preview = allRateTypePreviews[key];
                const isCurrentType = currentRateType === key;
                const hasData = preview?.hasRequiredData;
                const calculatedAmount = preview?.calculatedAmount || 0;

                return (
                  <div
                    key={key}
                    className={cn(
                      "p-4 border rounded-lg transition-colors",
                      isCurrentType && "border-primary bg-primary/5",
                      !hasData && "opacity-60"
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{label}</span>
                            {isCurrentType && (
                              <Badge variant="secondary" className="text-xs">
                                Current
                              </Badge>
                            )}
                          </div>
                          {hasData ? (
                            <p className="text-sm text-muted-foreground mt-1">
                              {preview.formula || `Rate: ${laborRate} ${unit}`}
                            </p>
                          ) : (
                            <p className="text-sm text-amber-600 mt-1">
                              {preview?.missingField
                                ? `Missing: ${preview.missingField}`
                                : "Cannot calculate"}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        {hasData ? (
                          <>
                            <p className="text-xl font-bold text-green-600">
                              {formatCurrency(calculatedAmount)}
                            </p>
                            {!isCurrentType && onApplyRateType && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="mt-2"
                                onClick={() =>
                                  onApplyRateType(
                                    key,
                                    preview.rate || laborRate
                                  )
                                }
                              >
                                Apply
                              </Button>
                            )}
                          </>
                        ) : (
                          <p className="text-muted-foreground">-</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Stored Pricing */}
          {storedPricing && (
            <div className="p-4 border rounded-lg">
              <h4 className="font-medium text-sm mb-2">
                Current Stored Pricing
              </h4>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Rate Type:</span>
                  <p className="font-medium">
                    {getRateTypeLabel(storedPricing.laborRateType)}
                  </p>
                </div>
                <div>
                  <span className="text-muted-foreground">Rate:</span>
                  <p className="font-medium">₹{storedPricing.laborRate}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">
                    Calculated Labor:
                  </span>
                  <p className="font-medium text-green-600">
                    {formatCurrency(
                      storedPricing.actualLabor || storedPricing.estimatedLabor
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const JobPaymentsTab = ({
  job,
  jobPaymentData = {},
  payments: legacyPayments = [],
  jobPaymentCalculation,
  pricingPreview,
  loading = {},
  onUpdatePricing,
  onAddDeduction,
  onRemoveDeduction,
  onCalculatePayment,
  onFetchPricingPreview,
  onCreatePayment,
  onProcessPayment,
  onCancelPayment,
  onRefresh,
}) => {
  // Dialog states
  const [showPricingDialog, setShowPricingDialog] = useState(false);
  const [showDeductionDialog, setShowDeductionDialog] = useState(false);
  const [showCalculationDialog, setShowCalculationDialog] = useState(false);
  const [showCreatePaymentDialog, setShowCreatePaymentDialog] = useState(false);
  const [showProcessPaymentDialog, setShowProcessPaymentDialog] =
    useState(false);
  const [showPricingPreviewDialog, setShowPricingPreviewDialog] =
    useState(false);
  const [showCancelPaymentDialog, setShowCancelPaymentDialog] = useState(false);
  const [showRemoveDeductionDialog, setShowRemoveDeductionDialog] =
    useState(false);

  // Selected items
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [selectedDeductionIndex, setSelectedDeductionIndex] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // ============================================================================
  // DATA EXTRACTION WITH PROPER FALLBACKS
  // ============================================================================

  const {
    pricing: jobPaymentPricing,
    karigarPayment: karigarPaymentData,
    payments: structuredPayments,
    materialValue: matValue = 0,
    wastageValue: wastValue = 0,
  } = jobPaymentData || {};

  // Safer pricing extraction - handle both API response and job.pricing
  const pricing = jobPaymentPricing || job?.pricing || {};
  const completionDetails = job?.completionDetails || {};
  const karigarPayment = karigarPaymentData || {};

  // Normalize deductions - handle both 'type' and 'deductionType' fields
  const deductions = (pricing?.deductions || []).map(normalizeDeduction);

  // ============================================================================
  // CALCULATE LABOR CHARGE WITH ALL FALLBACKS
  // ============================================================================

  const laborCharge = (() => {
    // 1. Try karigarPayment.grossAmount first (from backend calculation)
    if (karigarPayment.grossAmount > 0) return karigarPayment.grossAmount;

    // 2. Try pricing.actualLabor (frontend naming)
    if (pricing.actualLabor > 0) return pricing.actualLabor;

    // 3. Try pricing.laborAmount (backend naming)
    if (pricing.laborAmount > 0) return pricing.laborAmount;

    // 4. Try pricing.estimatedLabor
    if (pricing.estimatedLabor > 0) return pricing.estimatedLabor;

    // 5. Try to calculate from rate if we have the required data
    if (pricing.laborRate && pricing.laborRateType) {
      const weight =
        completionDetails.finalWeight ||
        job?.materialsReturned?.finishedWeight ||
        job?.specifications?.weight ||
        0;

      switch (pricing.laborRateType) {
        case "PER_GRAM":
          if (weight > 0) return weight * pricing.laborRate;
          break;
        case "FIXED":
          return pricing.laborRate;
        case "PER_PIECE":
          return (job?.pieceCount || 1) * pricing.laborRate;
        case "HOURLY":
          if (job?.actualHours > 0) return job.actualHours * pricing.laborRate;
          break;
        case "PERCENTAGE":
          // Would need material value
          break;
      }
    }

    return 0;
  })();

  // ============================================================================
  // COMMISSION CALCULATION
  // ============================================================================

  const commissionRate =
    karigarPayment.commissionRate ?? pricing.commissionRate ?? 0;

  const commission =
    karigarPayment.commissionAmount ??
    pricing.commissionAmount ??
    (laborCharge > 0 ? Math.round((laborCharge * commissionRate) / 100) : 0);

  // ============================================================================
  // DEDUCTIONS & ADDITIONAL CHARGES
  // ============================================================================

  const totalDeductions =
    pricing.totalDeductions ??
    deductions.reduce((sum, d) => sum + (d.amount || 0), 0);

  const additionalChargesArray = pricing.additionalCharges || [];
  const additionalCharges =
    pricing.totalAdditionalCharges ??
    additionalChargesArray.reduce((sum, c) => sum + (c.amount || 0), 0);

  // ============================================================================
  // NET PAYABLE CALCULATION
  // ============================================================================

  const netPayable = (() => {
    // 1. Prefer backend calculation if available and > 0
    if (karigarPayment.totalPayable > 0) return karigarPayment.totalPayable;
    if (karigarPayment.netAmount > 0) return karigarPayment.netAmount;

    // 2. Calculate locally
    const calculated =
      laborCharge - commission - totalDeductions + additionalCharges;
    return Math.max(0, calculated);
  })();

  // ============================================================================
  // PAYMENTS ARRAYS
  // ============================================================================

  const completedPayments =
    structuredPayments?.completed ||
    legacyPayments.filter((p) => isPaidStatus(p.status));

  const pendingPayments =
    structuredPayments?.pending ||
    legacyPayments.filter(
      (p) => isPendingStatus(p.status) || p.status?.toUpperCase() === "ON_HOLD"
    );

  // ============================================================================
  // TOTAL PAID & PENDING AMOUNTS
  // ============================================================================

  const totalPaid =
    karigarPayment.totalPaid ??
    completedPayments.reduce(
      (sum, p) => sum + (p.netAmount || p.grossAmount || p.amount || 0),
      0
    );

  // Pending amount calculation with smart handling
  const pendingAmount = (() => {
    // If backend provides it, use it
    if (karigarPayment.pendingAmount !== undefined) {
      return karigarPayment.pendingAmount;
    }

    // If netPayable is 0 but we have payments, don't show negative
    // This handles the case where labor hasn't been calculated yet
    if (netPayable === 0 && totalPaid > 0) {
      return 0;
    }

    return Math.max(0, netPayable - totalPaid);
  })();

  const pendingPaymentsTotal =
    karigarPayment.pendingPaymentsTotal ??
    pendingPayments.reduce(
      (sum, p) => sum + (p.netAmount || p.grossAmount || p.amount || 0),
      0
    );

  // ============================================================================
  // PAYMENT STATUS
  // ============================================================================

  const paymentStatus = (() => {
    // 1. Use backend status if available
    if (karigarPayment.paymentStatus) return karigarPayment.paymentStatus;

    // 2. Use isFullyPaid boolean
    if (karigarPayment.isFullyPaid) return "PAID";

    // 3. Calculate based on amounts
    // Special case: if netPayable is 0 but we have payments, consider it paid
    if (netPayable === 0 && totalPaid > 0) return "PAID";

    if (totalPaid >= netPayable && netPayable > 0) return "PAID";
    if (totalPaid > 0) return "PARTIAL";
    return "UNPAID";
  })();

  const isFullyPaid = paymentStatus === "PAID";
  const materialValue = matValue || 0;
  const wastageValue = wastValue || 0;

  // ============================================================================
  // PERMISSION FLAGS
  // ============================================================================

  const jobStatus = job?.status?.toUpperCase();
  const canSetPricing = !["PENDING", "CANCELLED"].includes(jobStatus);
  const canAddDeduction = !["PENDING", "CANCELLED", "DELIVERED"].includes(
    jobStatus
  );
  const hasKarigar = !!(job?.karigarId?._id || job?.karigarId);
  const canCreatePayment = !["CANCELLED"].includes(jobStatus) && hasKarigar;
  const hasPendingPayment = pendingPayments.length > 0;
  const isEarlyStage = [
    "PENDING",
    "ASSIGNED",
    "ACCEPTED",
    "IN_PROGRESS",
  ].includes(jobStatus);

  const isLoading =
    loading.pricing ||
    loading.deduction ||
    loading.removeDeduction ||
    loading.calculate ||
    loading.payment ||
    loading.cancel;

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleUpdatePricing = async (data) => {
    setSubmitting(true);
    try {
      await onUpdatePricing(job._id, data);
      setShowPricingDialog(false);
      toast.success("Pricing updated successfully");
      onRefresh?.();
    } catch (error) {
      toast.error(error?.message || "Failed to update pricing");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddDeduction = async (data) => {
    setSubmitting(true);
    try {
      await onAddDeduction(job._id, data);
      setShowDeductionDialog(false);
      toast.success("Deduction added successfully");
      onRefresh?.();
    } catch (error) {
      toast.error(error?.message || "Failed to add deduction");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveDeduction = async () => {
    if (selectedDeductionIndex === null) return;
    setSubmitting(true);
    try {
      await onRemoveDeduction(job._id, selectedDeductionIndex);
      setShowRemoveDeductionDialog(false);
      setSelectedDeductionIndex(null);
      toast.success("Deduction removed successfully");
      onRefresh?.();
    } catch (error) {
      toast.error(error?.message || "Failed to remove deduction");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCalculatePayment = async () => {
    setSubmitting(true);
    try {
      await onCalculatePayment(job._id);
      setShowCalculationDialog(true);
    } catch (error) {
      toast.error(error?.message || "Failed to calculate payment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleFetchPricingPreview = async () => {
    setSubmitting(true);
    try {
      await onFetchPricingPreview(job._id);
      setShowPricingPreviewDialog(true);
    } catch (error) {
      toast.error(error?.message || "Failed to fetch pricing preview");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreatePayment = async (data) => {
    if (!data.amount || isNaN(data.amount) || data.amount <= 0) {
      toast.error("Invalid payment amount");
      return;
    }
    setSubmitting(true);
    try {
      const karigarId = job.karigarId?._id || job.karigarId;
      const paymentData = {
        karigarId,
        paymentType: data.paymentType || "JOB_PAYMENT",
        grossAmount: Number(data.amount),
        jobId: job._id,
        jobNumber: job.jobNumber,
        branchId: job.branchId,
        branchCode: job.branchCode,
        branchName: job.branchName,
        notes: data.notes || undefined,
      };
      await onCreatePayment(paymentData);
      setShowCreatePaymentDialog(false);
      toast.success(
        `Payment of ${formatCurrency(data.amount)} created successfully`
      );
      onRefresh?.();
    } catch (error) {
      toast.error(error?.message || "Failed to create payment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleProcessPayment = async (data) => {
    if (!selectedPayment) return;
    setSubmitting(true);
    try {
      await onProcessPayment(selectedPayment._id, data);
      setShowProcessPaymentDialog(false);
      setSelectedPayment(null);
      toast.success("Payment processed successfully");
      onRefresh?.();
    } catch (error) {
      toast.error(error?.message || "Failed to process payment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelPayment = async () => {
    if (!selectedPayment || !cancelReason.trim()) {
      toast.error("Please provide a reason for cancellation");
      return;
    }
    setSubmitting(true);
    try {
      await onCancelPayment(selectedPayment._id, cancelReason);
      setShowCancelPaymentDialog(false);
      setSelectedPayment(null);
      setCancelReason("");
      toast.success("Payment cancelled successfully");
      onRefresh?.();
    } catch (error) {
      toast.error(error?.message || "Failed to cancel payment");
    } finally {
      setSubmitting(false);
    }
  };

  const openProcessDialog = (payment) => {
    setSelectedPayment(payment);
    setShowProcessPaymentDialog(true);
  };

  const openCancelDialog = (payment) => {
    setSelectedPayment(payment);
    setCancelReason("");
    setShowCancelPaymentDialog(true);
  };

  const openRemoveDeductionDialog = (index) => {
    setSelectedDeductionIndex(index);
    setShowRemoveDeductionDialog(true);
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Labor Charge Card */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Labor Charge</p>
                <p className="text-2xl font-bold">
                  {laborCharge > 0 ? formatCurrency(laborCharge) : "-"}
                </p>
              </div>
              <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Calculator className="h-5 w-5 text-blue-600" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {getRateTypeLabel(pricing.laborRateType)}
              {pricing.laborRate > 0 && (
                <span>
                  {" "}
                  @ ₹{pricing.laborRate}
                  {getRateTypeUnit(pricing.laborRateType) !== "₹" &&
                    `${getRateTypeUnit(pricing.laborRateType)}`}
                </span>
              )}
            </p>
            {laborCharge === 0 && pricing.laborRate > 0 && (
              <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                <Info className="h-3 w-3" />
                Awaiting final weight
              </p>
            )}
          </CardContent>
        </Card>

        {/* Deductions Card */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Deductions</p>
                <p className="text-2xl font-bold text-red-600">
                  {totalDeductions + commission > 0
                    ? `-${formatCurrency(totalDeductions + commission)}`
                    : "-"}
                </p>
              </div>
              <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                <TrendingDown className="h-5 w-5 text-red-600" />
              </div>
            </div>
            <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
              {commission > 0 && (
                <p>
                  Commission: {commissionRate}% ({formatCurrency(commission)})
                </p>
              )}
              {deductions.length > 0 && (
                <p>
                  {deductions.length} deduction(s):{" "}
                  {formatCurrency(totalDeductions)}
                </p>
              )}
              {commission === 0 && deductions.length === 0 && (
                <p>No deductions</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Net Payable Card */}
        <Card className="border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Net Payable</p>
                <p className="text-2xl font-bold text-green-600">
                  {netPayable > 0 ? formatCurrency(netPayable) : "-"}
                </p>
              </div>
              <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                <Wallet className="h-5 w-5 text-green-600" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {netPayable > 0 ? "After all deductions" : "Not yet calculated"}
            </p>
          </CardContent>
        </Card>

        {/* Paid Amount Card */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Paid Amount</p>
                <p className="text-2xl font-bold">
                  {totalPaid > 0 ? formatCurrency(totalPaid) : "-"}
                </p>
              </div>
              <div
                className={`h-10 w-10 rounded-full flex items-center justify-center ${
                  isFullyPaid
                    ? "bg-green-100"
                    : totalPaid > 0
                    ? "bg-yellow-100"
                    : "bg-gray-100"
                }`}
              >
                {isFullyPaid ? (
                  <CheckCircle className="h-5 w-5 text-green-600" />
                ) : totalPaid > 0 ? (
                  <Clock className="h-5 w-5 text-yellow-600" />
                ) : (
                  <Receipt className="h-5 w-5 text-gray-400" />
                )}
              </div>
            </div>
            <div className="mt-1">
              {pendingAmount > 0 && (
                <p className="text-xs text-amber-600">
                  Pending: {formatCurrency(pendingAmount)}
                </p>
              )}
              {isFullyPaid && (
                <Badge className="bg-green-100 text-green-800 text-xs">
                  Fully Paid
                </Badge>
              )}
              {paymentStatus === "PARTIAL" && (
                <Badge className="bg-yellow-100 text-yellow-800 text-xs">
                  Partial
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Payments Alert */}
      {hasPendingPayment && (
        <div className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <AlertTriangle className="h-4 w-4 text-yellow-600" />
          <span className="text-sm text-yellow-800">
            {pendingPayments.length} pending payment(s) totaling{" "}
            {formatCurrency(pendingPaymentsTotal)} awaiting processing.
          </span>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-3 flex-wrap">
        {canSetPricing && (
          <Button
            variant="outline"
            onClick={() => setShowPricingDialog(true)}
            disabled={isLoading}
          >
            <Edit className="h-4 w-4 mr-2" />
            {pricing.laborRate ? "Update Pricing" : "Set Pricing"}
          </Button>
        )}
        {canAddDeduction && (
          <Button
            variant="outline"
            onClick={() => setShowDeductionDialog(true)}
            disabled={isLoading}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Deduction
          </Button>
        )}
        {pricing.laborRate > 0 && (
          <Button
            variant="outline"
            onClick={handleCalculatePayment}
            disabled={isLoading || submitting}
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Calculator className="h-4 w-4 mr-2" />
            )}
            Calculate
          </Button>
        )}
        {canSetPricing && (
          <Button
            variant="outline"
            onClick={handleFetchPricingPreview}
            disabled={isLoading || submitting || loading.pricingPreview}
          >
            {loading.pricingPreview ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Eye className="h-4 w-4 mr-2" />
            )}
            Preview Pricing
          </Button>
        )}
        {canCreatePayment && (
          <Button
            onClick={() => setShowCreatePaymentDialog(true)}
            disabled={isLoading}
          >
            <Receipt className="h-4 w-4 mr-2" />
            Create Payment
          </Button>
        )}
        <Button variant="ghost" onClick={onRefresh} disabled={isLoading}>
          <RefreshCw
            className={`h-4 w-4 mr-2 ${isLoading ? "animate-spin" : ""}`}
          />
          Refresh
        </Button>
      </div>

      {/* Pricing Details Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Calculator className="h-4 w-4" />
            Pricing Configuration
          </CardTitle>
          <CardDescription>
            Labor rates and charges for this job
          </CardDescription>
        </CardHeader>
        <CardContent>
          {pricing.laborRate || pricing.laborRateType ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Rate Type</p>
                  <p className="font-medium">
                    {getRateTypeLabel(pricing.laborRateType)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Labor Rate</p>
                  <p className="font-medium">
                    {pricing.laborRate > 0 ? (
                      <>
                        ₹{pricing.laborRate}
                        <span className="text-muted-foreground text-xs ml-1">
                          {getRateTypeUnit(pricing.laborRateType)}
                        </span>
                      </>
                    ) : (
                      "-"
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    Commission Rate
                  </p>
                  <p className="font-medium">
                    {commissionRate > 0 ? `${commissionRate}%` : "-"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Final Weight</p>
                  <p className="font-medium">
                    {formatWeight(
                      completionDetails.finalWeight ||
                        job?.materialsReturned?.finishedWeight
                    )}
                  </p>
                </div>
              </div>

              {/* Calculation Breakdown */}
              {(laborCharge > 0 || totalPaid > 0) && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-sm font-medium mb-2">
                    Calculation Breakdown
                  </p>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span>Labor Charge</span>
                      <span className="font-medium">
                        {laborCharge > 0 ? formatCurrency(laborCharge) : "-"}
                      </span>
                    </div>
                    {commission > 0 && (
                      <div className="flex justify-between text-red-600">
                        <span>Commission ({commissionRate}%)</span>
                        <span>-{formatCurrency(commission)}</span>
                      </div>
                    )}
                    {totalDeductions > 0 && (
                      <div className="flex justify-between text-red-600">
                        <span>Other Deductions ({deductions.length})</span>
                        <span>-{formatCurrency(totalDeductions)}</span>
                      </div>
                    )}
                    {additionalCharges > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span>Additional Charges</span>
                        <span>+{formatCurrency(additionalCharges)}</span>
                      </div>
                    )}
                    <Separator className="my-2" />
                    <div className="flex justify-between font-bold">
                      <span>Net Payable</span>
                      <span className="text-green-600">
                        {netPayable > 0 ? formatCurrency(netPayable) : "-"}
                      </span>
                    </div>
                    {totalPaid > 0 && (
                      <>
                        <div className="flex justify-between text-blue-600">
                          <span>Total Paid</span>
                          <span>-{formatCurrency(totalPaid)}</span>
                        </div>
                        <Separator className="my-2" />
                        <div className="flex justify-between font-bold">
                          <span>Balance</span>
                          <span
                            className={
                              pendingAmount > 0
                                ? "text-amber-600"
                                : "text-green-600"
                            }
                          >
                            {pendingAmount > 0
                              ? formatCurrency(pendingAmount)
                              : "Settled"}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-6">
              <AlertCircle className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
              <p className="text-muted-foreground">
                Pricing not configured yet
              </p>
              {canSetPricing && (
                <Button
                  variant="outline"
                  className="mt-3"
                  onClick={() => setShowPricingDialog(true)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Set Pricing
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Deductions List */}
      {deductions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingDown className="h-4 w-4" />
              Deductions ({deductions.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {deductions.map((d, idx) => (
                  <TableRow key={d._id || idx}>
                    <TableCell>
                      <Badge variant="outline">
                        {d.type?.replace(/_/g, " ")}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-xs truncate">
                      {d.reason || d.description || "-"}
                    </TableCell>
                    <TableCell className="text-right text-red-600 font-medium">
                      -{formatCurrency(d.amount)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {d.createdAt
                        ? format(new Date(d.createdAt), "dd MMM yyyy")
                        : "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      {canAddDeduction && onRemoveDeduction && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => openRemoveDeductionDialog(idx)}
                          disabled={loading.removeDeduction}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Pending Payments Section */}
      {pendingPayments.length > 0 && (
        <Card className="border-yellow-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="h-4 w-4 text-yellow-600" />
              Pending Payments ({pendingPayments.length})
            </CardTitle>
            <CardDescription>Payments awaiting processing</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Reference</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingPayments.map((payment) => {
                  const statusConfig = getStatusConfig(payment.status);
                  const paymentAmount =
                    payment.netAmount ||
                    payment.grossAmount ||
                    payment.amount ||
                    0;
                  return (
                    <TableRow key={payment._id}>
                      <TableCell className="font-medium">
                        {payment.paymentReference ||
                          payment.paymentNumber ||
                          payment._id?.slice(-6)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {payment.paymentType?.replace(/_/g, " ") || "Payment"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(paymentAmount)}
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={`${statusConfig.bg} ${statusConfig.text}`}
                        >
                          {statusConfig.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {payment.createdAt
                          ? format(new Date(payment.createdAt), "dd MMM yyyy")
                          : "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          {isPendingStatus(payment.status) && (
                            <Button
                              size="sm"
                              onClick={() => openProcessDialog(payment)}
                            >
                              <CreditCard className="h-3 w-3 mr-1" />
                              Pay
                            </Button>
                          )}
                          {canCancelPayment(payment.status) &&
                            onCancelPayment && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => openCancelDialog(payment)}
                              >
                                <Ban className="h-3 w-3 mr-1" />
                                Cancel
                              </Button>
                            )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Completed Payments Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Receipt className="h-4 w-4" />
            Payment History
          </CardTitle>
          <CardDescription>Completed payments for this job</CardDescription>
        </CardHeader>
        <CardContent>
          {completedPayments.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Reference</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Paid At</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {completedPayments.map((payment) => {
                  const statusConfig = getStatusConfig(payment.status);
                  const paymentAmount =
                    payment.netAmount ||
                    payment.grossAmount ||
                    payment.amount ||
                    0;
                  return (
                    <TableRow key={payment._id}>
                      <TableCell className="font-medium">
                        {payment.paymentReference ||
                          payment.paymentNumber ||
                          payment._id?.slice(-6)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {payment.paymentType?.replace(/_/g, " ") || "Payment"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-medium text-green-600">
                        {formatCurrency(paymentAmount)}
                      </TableCell>
                      <TableCell>
                        {payment.paymentMethod?.replace(/_/g, " ") || "-"}
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={`${statusConfig.bg} ${statusConfig.text}`}
                        >
                          {statusConfig.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {payment.paidAt
                          ? format(new Date(payment.paidAt), "dd MMM yyyy")
                          : payment.paymentDate
                          ? format(new Date(payment.paymentDate), "dd MMM yyyy")
                          : payment.processedAt
                          ? format(new Date(payment.processedAt), "dd MMM yyyy")
                          : "-"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8">
              <Receipt className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">No completed payments yet</p>
              {canCreatePayment && (
                <Button
                  variant="outline"
                  className="mt-3"
                  onClick={() => setShowCreatePaymentDialog(true)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create First Payment
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ================================================================== */}
      {/* DIALOGS                                                            */}
      {/* ================================================================== */}

      {/* Pricing Dialog */}
      <PricingDialog
        open={showPricingDialog}
        onOpenChange={setShowPricingDialog}
        onSubmit={handleUpdatePricing}
        submitting={submitting}
        currentPricing={pricing}
        job={job}
      />

      {/* Deduction Dialog */}
      <DeductionDialog
        open={showDeductionDialog}
        onOpenChange={setShowDeductionDialog}
        onSubmit={handleAddDeduction}
        submitting={submitting}
      />

      {/* Remove Deduction Confirmation */}
      <AlertDialog
        open={showRemoveDeductionDialog}
        onOpenChange={setShowRemoveDeductionDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Deduction</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove this deduction?
            </AlertDialogDescription>
          </AlertDialogHeader>
          {selectedDeductionIndex !== null &&
            deductions[selectedDeductionIndex] && (
              <div className="p-4 bg-muted rounded-lg">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Type:</span>
                  <span className="font-medium">
                    {deductions[selectedDeductionIndex].type?.replace(
                      /_/g,
                      " "
                    )}
                  </span>
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-muted-foreground">Amount:</span>
                  <span className="font-medium text-red-600">
                    {formatCurrency(deductions[selectedDeductionIndex].amount)}
                  </span>
                </div>
                {deductions[selectedDeductionIndex].reason && (
                  <div className="mt-2 pt-2 border-t">
                    <span className="text-muted-foreground text-sm">
                      Reason:
                    </span>
                    <p className="text-sm mt-1">
                      {deductions[selectedDeductionIndex].reason}
                    </p>
                  </div>
                )}
              </div>
            )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={submitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveDeduction}
              disabled={submitting}
              className="bg-red-600 hover:bg-red-700"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Removing...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Remove
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel Payment Confirmation */}
      <AlertDialog
        open={showCancelPaymentDialog}
        onOpenChange={setShowCancelPaymentDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Payment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel this payment? This action cannot
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {selectedPayment && (
            <div className="p-4 bg-muted rounded-lg mb-4">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Reference:</span>
                <span className="font-medium">
                  {selectedPayment.paymentReference ||
                    selectedPayment._id?.slice(-6)}
                </span>
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-muted-foreground">Amount:</span>
                <span className="font-medium">
                  {formatCurrency(
                    selectedPayment.netAmount || selectedPayment.grossAmount
                  )}
                </span>
              </div>
            </div>
          )}
          <div>
            <Label htmlFor="cancelReason">Reason for cancellation *</Label>
            <Textarea
              id="cancelReason"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Enter reason..."
              rows={3}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={submitting}>
              Keep Payment
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelPayment}
              disabled={submitting || !cancelReason.trim()}
              className="bg-red-600 hover:bg-red-700"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Cancelling...
                </>
              ) : (
                <>
                  <Ban className="h-4 w-4 mr-2" />
                  Cancel Payment
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Calculation Dialog */}
      <CalculationDialog
        open={showCalculationDialog}
        onOpenChange={setShowCalculationDialog}
        calculation={jobPaymentCalculation}
        localCalculation={{
          laborCharge,
          commission,
          commissionRate,
          totalDeductions,
          netPayable,
        }}
        job={job}
        canCreatePayment={canCreatePayment}
        onCreatePayment={() => {
          setShowCalculationDialog(false);
          setShowCreatePaymentDialog(true);
        }}
      />

      {/* Create Payment Dialog */}
      <CreatePaymentDialog
        open={showCreatePaymentDialog}
        onOpenChange={setShowCreatePaymentDialog}
        onSubmit={handleCreatePayment}
        submitting={submitting}
        pendingAmount={pendingAmount}
        netPayable={netPayable}
        totalPaid={totalPaid}
        isEarlyStage={isEarlyStage}
        job={job}
      />

      {/* Process Payment Dialog */}
      <ProcessPaymentDialog
        open={showProcessPaymentDialog}
        onOpenChange={(open) => {
          setShowProcessPaymentDialog(open);
          if (!open) setSelectedPayment(null);
        }}
        onSubmit={handleProcessPayment}
        submitting={submitting}
        payment={selectedPayment}
      />

      {/* Pricing Preview Dialog */}
      <PricingPreviewDialog
        open={showPricingPreviewDialog}
        onOpenChange={setShowPricingPreviewDialog}
        pricingPreview={pricingPreview}
        job={job}
        onApplyRateType={(rateType, rate) => {
          onUpdatePricing(job._id, {
            laborRateType: rateType,
            laborRate: rate,
          });
          setShowPricingPreviewDialog(false);
        }}
      />
    </div>
  );
};

// ============================================================================
// PRICING DIALOG
// ============================================================================

const PricingDialog = ({
  open,
  onOpenChange,
  onSubmit,
  submitting,
  currentPricing,
  job,
}) => {
  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      laborRate: currentPricing?.laborRate || "",
      laborRateType: currentPricing?.laborRateType || "PER_GRAM",
      commissionRate: currentPricing?.commissionRate || 0,
    },
  });

  const watchRateType = watch("laborRateType");
  const watchRate = watch("laborRate");

  useEffect(() => {
    if (open) {
      reset({
        laborRate: currentPricing?.laborRate || "",
        laborRateType: currentPricing?.laborRateType || "PER_GRAM",
        commissionRate: currentPricing?.commissionRate || 0,
      });
    }
  }, [open, currentPricing, reset]);

  const finalWeight =
    job?.completionDetails?.finalWeight ||
    job?.materialsReturned?.finishedWeight ||
    job?.specifications?.weight ||
    0;

  const previewLabor =
    watchRateType === "PER_GRAM"
      ? finalWeight * (parseFloat(watchRate) || 0)
      : watchRateType === "FIXED"
      ? parseFloat(watchRate) || 0
      : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Set Job Pricing</DialogTitle>
          <DialogDescription>
            Configure labor rates for {job?.jobNumber}
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={handleSubmit((data) =>
            onSubmit({
              laborRate: parseFloat(data.laborRate),
              laborRateType: data.laborRateType,
              commissionRate: parseFloat(data.commissionRate) || 0,
            })
          )}
          className="space-y-4"
        >
          <div>
            <Label>Rate Type</Label>
            <Controller
              name="laborRateType"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LABOR_RATE_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>
          <div>
            <Label>Rate ({getRateTypeUnit(watchRateType)})</Label>
            <Input
              type="number"
              step="0.01"
              {...register("laborRate", {
                required: "Required",
                min: { value: 0, message: "Must be positive" },
              })}
            />
            {errors.laborRate && (
              <p className="text-sm text-red-500 mt-1">
                {errors.laborRate.message}
              </p>
            )}
          </div>
          <div>
            <Label>Commission Rate (%)</Label>
            <Input
              type="number"
              step="0.1"
              {...register("commissionRate", { min: 0, max: 100 })}
              placeholder="0"
            />
          </div>

          {/* Preview calculation */}
          {watchRateType === "PER_GRAM" &&
            finalWeight > 0 &&
            previewLabor > 0 && (
              <div className="p-3 bg-muted rounded-lg text-sm">
                <p className="text-muted-foreground mb-1">Preview:</p>
                <p>
                  {formatWeight(finalWeight)} × ₹{watchRate} ={" "}
                  <span className="font-medium text-green-600">
                    {formatCurrency(previewLabor)}
                  </span>
                </p>
              </div>
            )}

          {watchRateType === "PER_GRAM" && finalWeight === 0 && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
              <p className="text-amber-700 flex items-center gap-1">
                <AlertTriangle className="h-4 w-4" />
                Final weight not recorded yet. Labor will be calculated when
                weight is available.
              </p>
            </div>
          )}

          {watchRateType === "FIXED" && previewLabor > 0 && (
            <div className="p-3 bg-muted rounded-lg text-sm">
              <p className="text-muted-foreground mb-1">Preview:</p>
              <p>
                Fixed Amount:{" "}
                <span className="font-medium text-green-600">
                  {formatCurrency(previewLabor)}
                </span>
              </p>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================
// DEDUCTION DIALOG
// ============================================================================

const DeductionDialog = ({ open, onOpenChange, onSubmit, submitting }) => {
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: { type: "OTHER", amount: "", reason: "" },
  });

  useEffect(() => {
    if (!open) {
      reset({ type: "OTHER", amount: "", reason: "" });
    }
  }, [open, reset]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Deduction</DialogTitle>
          <DialogDescription>
            Add a deduction to this job's payment
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={handleSubmit((data) =>
            onSubmit({
              type: data.type,
              amount: parseFloat(data.amount),
              reason: data.reason,
            })
          )}
          className="space-y-4"
        >
          <div>
            <Label>Type</Label>
            <Controller
              name="type"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DEDUCTION_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>
          <div>
            <Label>Amount (₹)</Label>
            <Input
              type="number"
              step="0.01"
              {...register("amount", {
                required: "Required",
                min: { value: 0.01, message: "Must be positive" },
              })}
            />
            {errors.amount && (
              <p className="text-sm text-red-500 mt-1">
                {errors.amount.message}
              </p>
            )}
          </div>
          <div>
            <Label>Reason</Label>
            <Textarea
              {...register("reason", { required: "Required" })}
              rows={2}
              placeholder="Describe the reason for this deduction..."
            />
            {errors.reason && (
              <p className="text-sm text-red-500 mt-1">
                {errors.reason.message}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                "Add Deduction"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================
// CALCULATION DIALOG
// ============================================================================

const CalculationDialog = ({
  open,
  onOpenChange,
  calculation,
  localCalculation,
  job,
  canCreatePayment,
  onCreatePayment,
}) => {
  const data = calculation || localCalculation;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Payment Calculation</DialogTitle>
          <DialogDescription>{job?.jobNumber}</DialogDescription>
        </DialogHeader>
        {data ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Labor Charge</span>
                <span className="font-medium">
                  {formatCurrency(data.laborCharge || data.grossAmount || 0)}
                </span>
              </div>
              {(data.commission > 0 || data.commissionRate > 0) && (
                <div className="flex justify-between text-red-600">
                  <span>Commission ({data.commissionRate || 0}%)</span>
                  <span>-{formatCurrency(data.commission || 0)}</span>
                </div>
              )}
              {data.totalDeductions > 0 && (
                <div className="flex justify-between text-red-600">
                  <span>Deductions</span>
                  <span>-{formatCurrency(data.totalDeductions)}</span>
                </div>
              )}
            </div>
            <Separator />
            <div className="flex justify-between items-center">
              <span className="font-medium">Net Payable</span>
              <span className="text-2xl font-bold text-green-600">
                {formatCurrency(data.netPayable || 0)}
              </span>
            </div>

            {data.netPayable === 0 && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-700 flex items-center gap-1">
                  <AlertTriangle className="h-4 w-4" />
                  Net payable is zero. Ensure labor has been calculated.
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-4 text-muted-foreground">
            No calculation data available
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {canCreatePayment && data?.netPayable > 0 && (
            <Button onClick={onCreatePayment}>
              <Receipt className="h-4 w-4 mr-2" />
              Create Payment
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================
// CREATE PAYMENT DIALOG
// ============================================================================

const CreatePaymentDialog = ({
  open,
  onOpenChange,
  onSubmit,
  submitting,
  pendingAmount,
  netPayable,
  totalPaid,
  isEarlyStage,
  job,
}) => {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: { amount: "", paymentType: "JOB_PAYMENT", notes: "" },
  });

  useEffect(() => {
    if (open) {
      // Set suggested amount
      if (pendingAmount > 0) {
        setValue("amount", pendingAmount);
      } else if (netPayable > 0) {
        setValue("amount", netPayable);
      }
      // Set payment type based on stage
      setValue("paymentType", isEarlyStage ? "ADVANCE" : "JOB_PAYMENT");
    }
  }, [open, pendingAmount, netPayable, isEarlyStage, setValue]);

  useEffect(() => {
    if (!open) {
      reset({ amount: "", paymentType: "JOB_PAYMENT", notes: "" });
    }
  }, [open, reset]);

  const watchAmount = watch("amount");
  const watchPaymentType = watch("paymentType");
  const displayAmount =
    typeof watchAmount === "number"
      ? watchAmount
      : parseFloat(watchAmount) || 0;

  const karigarName =
    job?.karigarName ||
    job?.karigarId?.name ||
    (typeof job?.karigarId === "string" ? "Karigar" : "");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Payment</DialogTitle>
          <DialogDescription>
            {karigarName && `Payment for ${karigarName}`}
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={handleSubmit((data) => {
            const amt = parseFloat(data.amount);
            if (isNaN(amt) || amt <= 0) {
              toast.error("Invalid amount");
              return;
            }
            onSubmit({
              amount: amt,
              paymentType: data.paymentType,
              notes: data.notes,
            });
          })}
          className="space-y-4"
        >
          <div>
            <Label>Payment Type</Label>
            <Controller
              name="paymentType"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {isEarlyStage && watchPaymentType === "ADVANCE" && (
              <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                <Info className="h-3 w-3" />
                Advance payment before job completion
              </p>
            )}
          </div>

          <div>
            <Label>Amount (₹) *</Label>
            <Input
              type="number"
              step="0.01"
              min="1"
              {...register("amount", {
                required: "Required",
                valueAsNumber: true,
                validate: { positive: (v) => v > 0 || "Must be > 0" },
              })}
              className="text-lg font-medium"
            />
            {errors.amount && (
              <p className="text-sm text-red-500 mt-1">
                {errors.amount.message}
              </p>
            )}
            {(netPayable > 0 || totalPaid > 0) && (
              <p className="text-xs text-muted-foreground mt-1">
                {netPayable > 0 && `Net: ${formatCurrency(netPayable)}`}
                {netPayable > 0 && totalPaid > 0 && " | "}
                {totalPaid > 0 && `Paid: ${formatCurrency(totalPaid)}`}
                {pendingAmount > 0 &&
                  ` | Balance: ${formatCurrency(pendingAmount)}`}
              </p>
            )}
          </div>

          {displayAmount > 0 && (
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex justify-between items-center">
                <div>
                  <span className="font-medium text-green-800">
                    {watchPaymentType === "ADVANCE"
                      ? "Advance Payment"
                      : "Payment Amount"}
                  </span>
                  <p className="text-xs text-green-700 mt-0.5">
                    Job: {job?.jobNumber}
                  </p>
                </div>
                <span className="text-2xl font-bold text-green-600">
                  {formatCurrency(displayAmount)}
                </span>
              </div>
            </div>
          )}

          <div>
            <Label>Notes (optional)</Label>
            <Textarea
              {...register("notes")}
              rows={2}
              placeholder="Any additional notes..."
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting || displayAmount <= 0}
              className="bg-green-600 hover:bg-green-700"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Receipt className="h-4 w-4 mr-2" />
                  Create Payment
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================
// PROCESS PAYMENT DIALOG
// ============================================================================

const ProcessPaymentDialog = ({
  open,
  onOpenChange,
  onSubmit,
  submitting,
  payment,
}) => {
  const { register, handleSubmit, control, reset } = useForm({
    defaultValues: {
      paymentMethod: "CASH",
      transactionId: "",
      referenceNumber: "",
      notes: "",
    },
  });

  useEffect(() => {
    if (!open) {
      reset({
        paymentMethod: "CASH",
        transactionId: "",
        referenceNumber: "",
        notes: "",
      });
    }
  }, [open, reset]);

  if (!payment) return null;

  const paymentAmount =
    payment.netAmount || payment.grossAmount || payment.amount || 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Process Payment</DialogTitle>
          <DialogDescription>
            Mark payment as completed and record transaction details
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={handleSubmit((data) =>
            onSubmit({
              paymentMethod: data.paymentMethod,
              transactionId: data.transactionId || undefined,
              referenceNumber: data.referenceNumber || undefined,
              notes: data.notes || undefined,
            })
          )}
          className="space-y-4"
        >
          {/* Payment Summary */}
          <div className="p-4 bg-muted rounded-lg">
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Amount to Pay</span>
              <span className="text-xl font-bold text-green-600">
                {formatCurrency(paymentAmount)}
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Reference: {payment.paymentReference || payment._id?.slice(-6)}
            </p>
          </div>

          <div>
            <Label>Payment Method *</Label>
            <Controller
              name="paymentMethod"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map((m) => (
                      <SelectItem key={m.value} value={m.value}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>

          <div>
            <Label>Transaction ID</Label>
            <Input
              {...register("transactionId")}
              placeholder="UPI/Bank transaction ID"
            />
          </div>

          <div>
            <Label>Reference Number</Label>
            <Input
              {...register("referenceNumber")}
              placeholder="Cheque number, receipt number, etc."
            />
          </div>

          <div>
            <Label>Notes</Label>
            <Textarea
              {...register("notes")}
              rows={2}
              placeholder="Any additional notes..."
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-green-600 hover:bg-green-700"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Mark as Paid
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default JobPaymentsTab;
